/** Privacy policy signed checkbox on person contact edit. */

const PRIVACY_POLICY_STYLE_ID = 'impowerx-privacy-policy-signed-style'
const PRIVACY_POLICY_API = '/api/impowerX/contacts/privacy-policy-signed'

function ensurePrivacyPolicySignedStyles() {
  if (document.getElementById(PRIVACY_POLICY_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = PRIVACY_POLICY_STYLE_ID
  style.textContent = `
    .impowerx-privacy-policy-checkbox-row {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.85);
      cursor: pointer;
      width: fit-content;
      max-width: 100%;
      white-space: nowrap;
    }
    .impowerx-privacy-policy-checkbox-row .ant-checkbox {
      top: 0;
    }
    .impowerx-privacy-policy-checkbox-row .ant-checkbox-input {
      cursor: pointer;
    }
  `
  document.head.appendChild(style)
}

function cleanupPrivacyPolicySignedHost(existingHost, currentContactId) {
  if (!existingHost) return false
  // Keep the same-contact row whether loading, loaded, or failed. Remounting on
  // MutationObserver ticks aborts in-flight GETs and can loop forever with a
  // permanently disabled checkbox. Transient failures retry in-place instead.
  if (
    existingHost._contactId === currentContactId &&
    existingHost.dataset.privacyPolicyOwnRow === 'true'
  ) {
    return true
  }

  existingHost._privacyPolicyAbortController?.abort()
  if (existingHost.dataset.privacyPolicyOwnRow === 'true') {
    existingHost.remove()
    return false
  }

  // Clean up previous beside-Steuernummer placement in an empty column.
  existingHost.innerHTML = ''
  existingHost.classList.remove('impowerx-privacy-policy-host')
  existingHost.removeAttribute('data-privacy-policy-ui')
  delete existingHost._contactId
  delete existingHost._privacyPolicyAbortController
  delete existingHost.dataset.privacyPolicyLoaded
  return false
}

function createPrivacyPolicySignedRow(currentContactId) {
  const row = document.createElement('div')
  row.className = 'sub-section-content-row'
  row._contactId = currentContactId
  row.dataset.impowerxContactExtra = 'true'
  row.dataset.privacyPolicyOwnRow = 'true'
  row.setAttribute('data-privacy-policy-ui', 'host')
  row.innerHTML = `
    <div class="sub-section-content-column">
      <label
        class="ant-checkbox-wrapper ant-checkbox-wrapper-in-form-item ant-checkbox-group-item impowerx-privacy-policy-checkbox-row"
        for="impowerx-privacy-policy-signed"
        data-field-name="privacyPolicySigned"
        data-privacy-policy-ui="label"
      >
        <span class="ant-checkbox ant-checkbox-disabled" data-privacy-policy-ui="box">
          <input
            id="impowerx-privacy-policy-signed"
            type="checkbox"
            class="ant-checkbox-input"
            data-field-name="privacyPolicySigned"
            data-privacy-policy-ui="input"
            disabled
          />
          <span class="ant-checkbox-inner"></span>
        </span>
        <span>Datenschutzerklärung unterschrieben</span>
      </label>
    </div>
    <div class="sub-section-content-column"></div>
  `
  return row
}

/** In-memory GET cache so MutationObserver remounts don't hammer the API. */
const privacyPolicySignedCache = new Map()

function getPrivacyPolicyAuthToken() {
  try {
    return JSON.parse(localStorage.getItem('auth') || '{}')?.token || null
  } catch {
    return null
  }
}

function isContactSaveAction(buttonElement) {
  if (!buttonElement) return false
  if (buttonElement.getAttribute('type') === 'submit') return true
  const label = buttonElement.textContent?.toLowerCase().trim() || ''
  return label.includes('speichern') || label.includes('save')
}

function abortError() {
  const error = new Error('Aborted')
  error.name = 'AbortError'
  return error
}

function awaitUnlessAborted(promise, signal) {
  if (!signal) return promise
  if (signal.aborted) return Promise.reject(abortError())
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError())
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (error) => {
        signal.removeEventListener('abort', onAbort)
        reject(error)
      }
    )
  })
}

async function fetchPrivacyPolicySigned(contactId, signal) {
  const cacheKey = String(contactId)
  const cached = privacyPolicySignedCache.get(cacheKey)
  if (cached?.status === 'ok') {
    return { ok: true, privacyPolicySigned: cached.value === true }
  }
  if (cached?.status === 'pending' && cached.promise) {
    // Caller abort must not cancel the shared network request — remounts
    // would otherwise abort+restart the same GET forever.
    return awaitUnlessAborted(cached.promise, signal)
  }

  const token = getPrivacyPolicyAuthToken()
  if (!token) return { ok: false, reason: 'no-token' }

  const promise = (async () => {
    try {
      const res = await fetch(
        `${baseApiUrl}${PRIVACY_POLICY_API}?contactId=${contactId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      if (!res.ok) {
        privacyPolicySignedCache.delete(cacheKey)
        return { ok: false, reason: 'http' }
      }
      const data = await res.json()
      const privacyPolicySigned = data.privacyPolicySigned === true
      privacyPolicySignedCache.set(cacheKey, {
        status: 'ok',
        value: privacyPolicySigned,
      })
      return { ok: true, privacyPolicySigned }
    } catch (error) {
      privacyPolicySignedCache.delete(cacheKey)
      throw error
    }
  })()

  privacyPolicySignedCache.set(cacheKey, {
    status: 'pending',
    promise,
  })

  return awaitUnlessAborted(promise, signal)
}

async function postPrivacyPolicySigned(contactId, privacyPolicySigned) {
  const token = getPrivacyPolicyAuthToken()
  if (!token) return false

  const res = await fetch(`${baseApiUrl}${PRIVACY_POLICY_API}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contactId, privacyPolicySigned }),
    keepalive: true,
  })
  if (!res.ok) {
    console.error('Save privacy policy signed preference failed:', res.status)
    return false
  }
  privacyPolicySignedCache.set(String(contactId), {
    status: 'ok',
    value: privacyPolicySigned === true,
  })
  return true
}

function wirePrivacyPolicySignedCheckbox({
  row,
  contactEditing,
  currentContactId,
  labelEl,
  boxEl,
  inputEl,
}) {
  // Stay disabled until a successful GET so Speichern cannot POST false and
  // wipe an existing signed record before load finishes (or after a failed GET).
  let isLoaded = false

  const syncCheckboxState = () => {
    labelEl?.classList.toggle('ant-checkbox-wrapper-checked', inputEl.checked)
    boxEl?.classList.toggle('ant-checkbox-checked', inputEl.checked)
    labelEl?.classList.toggle('ant-checkbox-wrapper-disabled', inputEl.disabled)
    boxEl?.classList.toggle('ant-checkbox-disabled', inputEl.disabled)
  }

  const setInputEnabled = (enabled) => {
    if (!document.body.contains(row)) return
    inputEl.disabled = !enabled
    syncCheckboxState()
  }

  const abortController = new AbortController()
  row._privacyPolicyAbortController = abortController
  setInputEnabled(false)

  const MAX_LOAD_ATTEMPTS = 3

  const loadPreference = async (attempt = 0) => {
    if (!document.body.contains(row) || row._contactId !== currentContactId) {
      return
    }
    setInputEnabled(false)
    try {
      const result = await fetchPrivacyPolicySigned(
        currentContactId,
        abortController.signal
      )
      if (!result.ok) {
        isLoaded = false
        delete row.dataset.privacyPolicyLoaded
        setInputEnabled(false)
        if (
          attempt + 1 < MAX_LOAD_ATTEMPTS &&
          document.body.contains(row) &&
          row._contactId === currentContactId
        ) {
          setTimeout(() => loadPreference(attempt + 1), 750 * (attempt + 1))
        }
        return
      }
      inputEl.checked = result.privacyPolicySigned
      isLoaded = true
      row.dataset.privacyPolicyLoaded = 'true'
      setInputEnabled(true)
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.error('Error loading privacy policy signed preference:', error)
      isLoaded = false
      delete row.dataset.privacyPolicyLoaded
      setInputEnabled(false)
      if (
        attempt + 1 < MAX_LOAD_ATTEMPTS &&
        document.body.contains(row) &&
        row._contactId === currentContactId
      ) {
        setTimeout(() => loadPreference(attempt + 1), 750 * (attempt + 1))
      }
    }
  }

  const savePreference = async () => {
    if (!isLoaded || inputEl.disabled) return
    try {
      await postPrivacyPolicySigned(currentContactId, Boolean(inputEl.checked))
    } catch (error) {
      console.error('Error saving privacy policy signed preference:', error)
    }
  }

  inputEl.addEventListener('change', () => {
    syncCheckboxState()
    if (isLoaded && !inputEl.disabled) savePreference()
  })

  document.addEventListener(
    'click',
    (event) => {
      const clickedButton = event.target.closest('button')
      if (!clickedButton || !contactEditing.contains(clickedButton)) return
      if (isContactSaveAction(clickedButton)) savePreference()
    },
    { signal: abortController.signal }
  )

  loadPreference()
}

function mountPrivacyPolicySignedField(
  contactEditing,
  personDiv,
  currentContactId
) {
  const existing = personDiv.querySelector(
    '[data-field-name="privacyPolicySigned"]'
  )
  const existingHost =
    existing?.closest('[data-privacy-policy-ui="host"]') ||
    existing?.closest('.sub-section-content-row')
  if (cleanupPrivacyPolicySignedHost(existingHost, currentContactId)) {
    return true
  }

  const taxRow = personDiv
    .querySelector('[data-field-name="taxNumber"]')
    ?.closest('.sub-section-content-row')
  const rows = personDiv
    .querySelector('.sub-section-content')
    ?.querySelectorAll('.sub-section-content-row')
  const lastRow = rows?.[rows.length - 1]
  if (!taxRow && !lastRow) {
    return false
  }

  ensurePrivacyPolicySignedStyles()
  const row = createPrivacyPolicySignedRow(currentContactId)
  // Directly under Steuernummer+Ehepartner row (above Kontaktrollen).
  ;(taxRow || lastRow).insertAdjacentElement('afterend', row)

  const labelEl = row.querySelector('[data-privacy-policy-ui="label"]')
  const boxEl = row.querySelector('[data-privacy-policy-ui="box"]')
  const inputEl = row.querySelector('[data-privacy-policy-ui="input"]')
  if (!inputEl) {
    return true
  }

  wirePrivacyPolicySignedCheckbox({
    row,
    contactEditing,
    currentContactId,
    labelEl,
    boxEl,
    inputEl,
  })
  return true
}

function initPrivacyPolicySignedField() {
  const contactEditing = document.querySelector('.ContactEditing')
  const personDiv = document.querySelector('#person')
  if (!contactEditing || !personDiv) {
    return false
  }

  const currentContactId =
    window.location.pathname.match(/\/edit-person\/(\d+)/)?.[1]
  if (!currentContactId) {
    return false
  }

  return mountPrivacyPolicySignedField(
    contactEditing,
    personDiv,
    currentContactId
  )
}

function initPrivacyPolicySignedViewSection() {
  const currentContactId =
    window.location.pathname.match(/\/persons\/view-person\/(\d+)/)?.[1] ||
    window.location.pathname.match(/\/view-person\/(\d+)/)?.[1]
  if (!currentContactId) {
    return false
  }

  const basicInfoSection = document.querySelector(
    '.PersonData .basic-info.section'
  )
  if (!basicInfoSection) {
    return false
  }

  const desiredPrivacyViewAnchor = () => {
    // Order: … → Ehepartner → Datenschutzerklärung → Rollen
    const ehepartnerBlock = basicInfoSection.querySelector(
      '[data-impowerx-view-ehepartner="true"]'
    )
    if (ehepartnerBlock) {
      return { type: 'after', node: ehepartnerBlock }
    }
    const rolesBlock = basicInfoSection.querySelector(
      '[data-impowerx-view-contact-roles="true"]'
    )
    if (rolesBlock) {
      return { type: 'before', node: rolesBlock }
    }
    return { type: 'append', node: basicInfoSection }
  }

  const isPrivacyViewInPlace = (block, anchor) => {
    if (anchor.type === 'after') {
      return block.previousElementSibling === anchor.node
    }
    if (anchor.type === 'before') {
      return block.nextElementSibling === anchor.node
    }
    return anchor.node.lastElementChild === block
  }

  const placePrivacyViewBlock = (block) => {
    const anchor = desiredPrivacyViewAnchor()
    // Avoid insertAdjacentElement when already correct — each move triggers the
    // person-view MutationObserver and can loop GETs via remount.
    if (isPrivacyViewInPlace(block, anchor)) return false
    if (anchor.type === 'after') {
      anchor.node.insertAdjacentElement('afterend', block)
    } else if (anchor.type === 'before') {
      anchor.node.insertAdjacentElement('beforebegin', block)
    } else {
      anchor.node.appendChild(block)
    }
    return true
  }

  const existing = document.querySelector(
    '[data-impowerx-view-privacy-policy-signed="true"]'
  )
  if (existing) {
    if (
      existing._contactId === currentContactId &&
      document.body.contains(existing)
    ) {
      placePrivacyViewBlock(existing)
      return true
    }
    existing.remove()
  }

  const block = document.createElement('div')
  block.dataset.impowerxViewPrivacyPolicySigned = 'true'
  block._contactId = currentContactId
  block.innerHTML = `
    <div class="label">Datenschutzerklärung unterschrieben</div>
    <div class="value" data-field-name="privacyPolicySignedViewValue">Lade...</div>
  `

  placePrivacyViewBlock(block)

  // One fetch per mounted block; remounts of an already-fetched contact are skipped above.
  ;(async () => {
    const valueEl = block.querySelector(
      '[data-field-name="privacyPolicySignedViewValue"]'
    )
    if (!valueEl) return
    if (block.dataset.privacyPolicyViewFetched === 'true') return
    block.dataset.privacyPolicyViewFetched = 'pending'

    try {
      const result = await fetchPrivacyPolicySigned(currentContactId)
      if (
        !document.body.contains(block) ||
        block._contactId !== currentContactId
      ) {
        return
      }
      if (!result.ok) {
        delete block.dataset.privacyPolicyViewFetched
        valueEl.textContent = 'Fehler beim Laden'
        return
      }
      block.dataset.privacyPolicyViewFetched = 'true'
      valueEl.textContent = result.privacyPolicySigned ? 'Ja' : 'Nein'
    } catch (error) {
      delete block.dataset.privacyPolicyViewFetched
      console.error('Error loading privacy policy signed view value:', error)
      if (document.body.contains(block)) {
        valueEl.textContent = 'Fehler beim Laden'
      }
    }
  })()

  return true
}

window.initPrivacyPolicySignedField = initPrivacyPolicySignedField
window.initPrivacyPolicySignedViewSection = initPrivacyPolicySignedViewSection
